<script setup lang="ts">
// Generic decision-tree walker: renders one node at a time from a tree
// contract, with back + progress. Wizard-style trees (next: null terminal)
// emit `complete`; troubleshooting trees land on an `outcome` node and emit
// `outcome`. The parent renders whatever completion UI fits the site — this
// component owns only the walk.
import { computed, ref, watch } from 'vue'
import { NButton, NCheckbox, NInputNumber } from 'naive-ui'
import { useDiagnosticEngine } from '@/shared/diagnostic/engine'
import type { Answer, DecisionTree, TreeNode } from '@/shared/diagnostic/types'

const props = withDefaults(
    defineProps<{
        tree: DecisionTree
        /** How the per-question "Why we ask this" copy is presented:
         *  'inline' = collapsible block under the inputs; 'aside' = side
         *  panel to the right of the question (stacks on mobile). */
        whyVariant?: 'inline' | 'aside'
        /** One-line data-use promise rendered inside the why widget. */
        dataUseNote?: string
    }>(),
    { whyVariant: 'inline', dataUseNote: undefined },
)

const emit = defineEmits<{
    complete: [answers: Answer[]]
    outcome: [outcomeId: string, node: TreeNode, answers: Answer[]]
}>()

const engine = useDiagnosticEngine(props.tree)
const { currentId, currentNode, outcome, answers, done, progress, canGoBack } = engine

const multiDraft = ref<string[]>([])
const numericDraft = ref<number | null>(null)

watch(currentId, () => {
    multiDraft.value = []
    numericDraft.value = null
})

watch(done, (isDone) => {
    if (!isDone) return
    if (outcome.value && currentId.value) {
        emit('outcome', currentId.value, outcome.value, answers.value)
    } else {
        emit('complete', answers.value)
    }
})

const numericValid = computed(() => {
    const node = currentNode.value
    if (!node || numericDraft.value === null) return false
    if (node.min !== undefined && numericDraft.value < node.min) return false
    if (node.max !== undefined && numericDraft.value > node.max) return false
    return true
})

function toggleMulti(value: string, checked: boolean) {
    multiDraft.value = checked
        ? [...multiDraft.value, value]
        : multiDraft.value.filter((v) => v !== value)
}

defineExpose({ reset: engine.reset })
</script>

<template>
    <div class="tree-walker">
        <div class="progress" role="progressbar" :aria-valuenow="Math.round(progress * 100)">
            <div class="bar" :style="{ width: `${Math.max(progress * 100, 4)}%` }"></div>
        </div>

        <div v-if="currentNode && !outcome" class="q-wrap" :class="{ 'has-aside': whyVariant === 'aside' && currentNode.why }">
            <div class="q-main">
            <p class="prompt">{{ currentNode.prompt }}</p>
            <p v-if="currentNode.help" class="muted help">{{ currentNode.help }}</p>

            <!-- single select: one tap advances -->
            <div v-if="currentNode.kind === 'single_select'" class="options">
                <button
                    v-for="opt in currentNode.options"
                    :key="opt.value"
                    type="button"
                    class="option"
                    @click="engine.answer(opt.value)"
                >
                    {{ opt.label }}
                </button>
            </div>

            <!-- multi select: draft + continue -->
            <div v-else-if="currentNode.kind === 'multi_select'" class="multi">
                <NCheckbox
                    v-for="opt in currentNode.options"
                    :key="opt.value"
                    :checked="multiDraft.includes(opt.value)"
                    @update:checked="(c: boolean) => toggleMulti(opt.value, c)"
                >
                    {{ opt.label }}
                </NCheckbox>
                <NButton type="primary" class="continue" @click="engine.answer([...multiDraft])">
                    Continue
                </NButton>
            </div>

            <!-- numeric -->
            <div v-else-if="currentNode.kind === 'numeric'" class="numeric">
                <NInputNumber
                    v-model:value="numericDraft"
                    :min="currentNode.min"
                    :max="currentNode.max"
                    :placeholder="currentNode.unit ?? 'Enter a number'"
                    style="max-width: 240px"
                />
                <NButton
                    type="primary"
                    :disabled="!numericValid"
                    @click="numericDraft !== null && engine.answer(numericDraft)"
                >
                    Continue
                </NButton>
            </div>

            <!-- transparency: inline variant -->
            <details v-if="whyVariant === 'inline' && currentNode.why" class="why-inline">
                <summary>Why we ask this</summary>
                <p>{{ currentNode.why }}</p>
                <p v-if="dataUseNote" class="data-use">{{ dataUseNote }}</p>
            </details>
            </div>

            <!-- transparency: aside variant -->
            <aside v-if="whyVariant === 'aside' && currentNode.why" class="why-aside" aria-label="Why we ask this">
                <p class="why-head">Why we ask this</p>
                <p class="why-body">{{ currentNode.why }}</p>
                <p v-if="dataUseNote" class="data-use">{{ dataUseNote }}</p>
            </aside>
        </div>

        <button v-if="canGoBack && !done" type="button" class="back muted" @click="engine.back()">
            ← Back
        </button>
    </div>
</template>

<style scoped lang="scss">
.tree-walker {
    .progress {
        height: 6px;
        border-radius: var(--site-radius-pill);
        background: var(--site-accent-soft);
        overflow: hidden;
        margin-bottom: var(--site-space-lg);

        .bar {
            height: 100%;
            background: var(--site-gradient-accent);
            border-radius: var(--site-radius-pill);
            transition: width 0.25s ease;
        }
    }

    .prompt {
        font-family: var(--site-font-heading);
        font-size: clamp(1.2rem, 2.6vw, 1.55rem);
        font-weight: 700;
        line-height: 1.3;
        margin-bottom: var(--site-space-md);
    }

    .help {
        margin-top: calc(-1 * var(--site-space-sm));
    }

    .options {
        display: grid;
        gap: var(--site-space-sm);
    }

    .option {
        text-align: left;
        padding: 0.9em 1.1em;
        font-family: var(--site-font-body);
        font-size: 1rem;
        color: var(--site-text);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-md);
        cursor: pointer;
        transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;

        &:hover {
            border-color: var(--site-accent);
            background: var(--site-accent-soft);
            transform: translateX(2px);
        }
    }

    .multi {
        display: grid;
        gap: var(--site-space-sm);
        justify-items: start;
    }

    .numeric {
        display: flex;
        gap: var(--site-space-sm);
        align-items: center;
        flex-wrap: wrap;
    }

    .continue {
        margin-top: var(--site-space-sm);
    }

    .q-wrap.has-aside {
        display: grid;
        grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
        gap: var(--site-space-lg);
        align-items: start;

        @media (max-width: 760px) {
            grid-template-columns: 1fr;
        }
    }

    .why-inline {
        margin-top: var(--site-space-lg);
        border-top: 1px dashed var(--site-border);
        padding-top: var(--site-space-sm);

        summary {
            cursor: pointer;
            font-size: 0.88rem;
            font-weight: 600;
            color: var(--site-accent);
            list-style: none;

            &::before {
                content: 'ⓘ ';
            }

            &::-webkit-details-marker {
                display: none;
            }
        }

        p {
            margin: var(--site-space-sm) 0 0;
            font-size: 0.92rem;
            color: var(--site-text-muted);
            max-width: 56ch;
        }
    }

    .why-aside {
        background: var(--site-accent-soft);
        border-radius: var(--site-radius-md);
        padding: var(--site-space-md) var(--site-space-lg);

        .why-head {
            font-size: 0.78rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: var(--site-accent);
            margin: 0 0 var(--site-space-sm);

            &::before {
                content: 'ⓘ ';
            }
        }

        .why-body {
            font-size: 0.94rem;
            margin: 0;
            color: var(--site-text);
        }
    }

    .data-use {
        font-size: 0.8rem !important;
        font-style: italic;
        color: var(--site-text-muted) !important;
        margin-top: var(--site-space-sm) !important;
    }

    .back {
        margin-top: var(--site-space-lg);
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--site-font-body);
        font-size: 0.9rem;

        &:hover {
            color: var(--site-accent);
        }
    }
}
</style>
